// src/components/sections/TestimonialsCarousel.jsx
import React, { useState, useRef, useEffect } from 'react'
import { motion as Motion, useInView, AnimatePresence } from "framer-motion";
import { FiStar } from "react-icons/fi";
import { fetchTestimonials } from '../../redux/slices/testimonialsSlice.js'
import { FaArrowRight, FaArrowLeft } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from '../../hooks/useAppDispatch.js'
import EmptyState from '../common/EmptyState.jsx'
import "./TestimonialSlider.scss";

export default function TestimonialsCarousel() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const sectionRef = useRef(null);
    const isInView = useInView(sectionRef, { amount: 0.4, once: false });

    const dispatch = useAppDispatch();

    // Get Redux state
    const { testimonials: testimonialsData, loading } = useAppSelector((state) => state.testimonials);
    const { testimonials: globalTestimonialsConfig } = useAppSelector((state) => state.global);

    // Extract testimonials array - check different possible structures
    const testimonials = Array.isArray(testimonialsData)
        ? testimonialsData
        : testimonialsData?.testimonials || testimonialsData?.data || [];

    // Fetch testimonials on component mount
    useEffect(() => {
        dispatch(fetchTestimonials({ limit: 6 }));
    }, [dispatch]);

    // Auto-play functionality
    useEffect(() => {
        let interval;
        if (isAutoPlaying && isInView && testimonials?.length > 0) {
            interval = setInterval(() => {
                goToNext();
            }, 3000);
        }

        return () => clearInterval(interval);
    }, [currentIndex, isAutoPlaying, isInView, testimonials?.length]);

    const goToPrev = () => {
        setIsAutoPlaying(false);
        if (testimonials.length > 0) {
            setCurrentIndex(prev =>
                prev === 0 ? testimonials.length - 1 : prev - 1
            );
        }
    };

    const goToNext = () => {
        setIsAutoPlaying(false);
        if (testimonials.length > 0) {
            setCurrentIndex(prev =>
                prev === testimonials.length - 1 ? 0 : prev + 1
            );
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                ease: "easeOut",
                when: "beforeChildren",
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" }
        }
    };

    const slideVariants = {
        hidden: { opacity: 0, x: 100 },
        visible: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -100 }
    };

    // Show loading skeleton
    if (loading && (!testimonials || testimonials.length === 0)) {
        return (
            <section className="testimonial-slider" ref={sectionRef}>
                <div className="container">
                    <Motion.div
                        className="slider-header"
                        initial="hidden"
                        animate="visible"
                        variants={containerVariants}
                    >
                        <div className="h-10 bg-black-200 rounded w-64 animate-pulse mb-4"></div>
                    </Motion.div>

                    <div className="slider-container">
                        <div className="testimonial-card active animate-pulse">
                            <div className="card-content">
                                <div className="h-32 bg-black-200 rounded mb-6"></div>
                                <div className="testimonial-footer">
                                    <div className="author-info">
                                        <div className="author-avatar">
                                            <div className="w-12 h-12 bg-black-200 rounded-full"></div>
                                        </div>
                                        <div>
                                            <div className="h-6 bg-black-200 rounded w-32 mb-2"></div>
                                            <div className="flex">
                                                {[...Array(5)].map((_, i) => (
                                                    <div key={i} className="w-5 h-5 bg-black-200 rounded mr-1"></div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="slider-indicators">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="indicator bg-black-200"></div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (!testimonials || testimonials.length === 0) {
        return (
            <section className="testimonial-slider" ref={sectionRef}>
                <div className="container">
                    <Motion.div
                        className="slider-header"
                        initial="hidden"
                        animate="visible"
                        variants={containerVariants}
                    >
                        <Motion.h2 variants={itemVariants}>
                            {`${globalTestimonialsConfig?.heading || "The Cutomer is Hero of Our Business"}`.toUpperCase()}
                        </Motion.h2>
                    </Motion.div>

                    <EmptyState
                        title="No Testimonials Yet"
                        message="Check back later for customer testimonials."
                        containerClassName="mt-8"
                    />
                </div>
            </section>
        );
    }

    const currentTestimonial = testimonials[currentIndex];

    // Handle image error
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        const nextSibling = e.target.nextElementSibling;
        if (nextSibling) {
            nextSibling.style.display = 'flex';
        }
    };

    return (
        <section className="testimonial-slider" ref={sectionRef}>
            <div className="container">
                <Motion.div
                    className="slider-header"
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={containerVariants}
                >
                    <Motion.h2 variants={itemVariants}>
                        {`${globalTestimonialsConfig?.heading || "The Cutomer is Hero of Our Business"}`.toUpperCase()}
                    </Motion.h2>
                    <Motion.div className="slider-controls" variants={itemVariants}>
                        <button onClick={goToPrev} aria-label="Previous testimonial">
                            <FaArrowLeft />
                        </button>
                        <button onClick={goToNext} aria-label="Next testimonial">
                            <FaArrowRight />
                        </button>
                    </Motion.div>
                </Motion.div>

                <div className="slider-container">
                    <AnimatePresence mode="wait">
                        <Motion.div
                            key={currentTestimonial?.id || currentIndex}
                            className="testimonial-card active"
                            variants={slideVariants}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                            exit="exit"
                            transition={{ duration: 0.5 }}
                        >
                            <div className="card-content">
                                <p className="testimonial-quote">
                                    "{currentTestimonial?.comment || currentTestimonial?.heading_1 || currentTestimonial?.quote || "Loading testimonial..."}"
                                </p>
                                <div className="testimonial-footer">
                                    <div className="author-info">
                                        <div className="author-avatar">
                                            {currentTestimonial?.imageUrl || currentTestimonial?.image || currentTestimonial?.avatar ? (
                                                <img
                                                    src={currentTestimonial.imageUrl || currentTestimonial.image || currentTestimonial.avatar}
                                                    alt={currentTestimonial?.name || "Testimonial author"}
                                                    onError={handleImageError}
                                                />
                                            ) : <div
                                                className={`avatar-placeholder ${(currentTestimonial?.imageUrl || currentTestimonial?.image || currentTestimonial?.avatar)
                                                    ? 'fallback'
                                                    : ''
                                                    }`}
                                            >
                                                {currentTestimonial?.name?.charAt(0) || "U"}
                                            </div>}
                                        </div>
                                        <div>
                                            <h4>{currentTestimonial?.name || "Anonymous"}</h4>
                                            <div className="rating">
                                                {[...Array(5)].map((_, i) => (
                                                    <FiStar
                                                        key={i}
                                                        className={i < (currentTestimonial?.rating || currentTestimonial?.stars || 5) ? 'filled' : ''}
                                                    />
                                                ))}
                                            </div>
                                            {(currentTestimonial?.role || currentTestimonial?.company || currentTestimonial?.designation) && (
                                                <p className="text-gray-600 text-sm mt-1">
                                                    {currentTestimonial?.role && currentTestimonial?.company
                                                        ? `${currentTestimonial.role}, ${currentTestimonial.company}`
                                                        : currentTestimonial?.role || currentTestimonial?.company || currentTestimonial?.designation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Motion.div>
                    </AnimatePresence>
                </div>

                {/* Progress indicators */}
                <div className="slider-indicators">
                    {testimonials.map((_, index) => (
                        <button
                            key={index}
                            className={`indicator ${index === currentIndex ? 'active' : ''}`}
                            onClick={() => {
                                setIsAutoPlaying(false);
                                setCurrentIndex(index);
                            }}
                            aria-label={`Go to testimonial ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Mobile controls */}
                <Motion.div
                    className="mobile-controls"
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    variants={itemVariants}
                >
                    <button onClick={goToPrev} aria-label="Previous testimonial">
                        <FaArrowLeft />
                    </button>
                    <button onClick={goToNext} aria-label="Next testimonial">
                        <FaArrowRight />
                    </button>
                </Motion.div>
            </div>
        </section>
    )
}